import { useResizablePanelStyles } from '@components/v2/layout/resizablePanel/resizablePanel.styles';
import Backdrop from '@components/v2/ui/Backdrop/Backdrop';
import ResizeSeparator from '@components/v2/ui/ResizeSeparator/ResizeSeparator';
import { Box, CSSProperties, Slide, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { Panel, PanelSize } from 'react-resizable-panels';
import LangEnum from '../../../../../../lang/LangEnum';
import { DetailsPanelDisplayContext } from '../../contexts/DetailsPanelDisplay.context';
import { OBJECT_DETAILS_PANEL_ID } from '../../objectScreen.constants';
import { DETAILS_PANEL_WIDTH } from '../DetailsPanel';
import { useObjectDetailsSlot } from './hooks/useObjectDetailsSlot';
import { getDetailsExpansionSteps } from './objectDetailsSlot.utils';

interface ObjectDetailsSlotProps {
  panel: ReactNode;
  isOpen: boolean;
  isStickyLayout?: boolean;
  stickyTop?: number;
  height?: number;
  onClose: () => void;
  onExpansionStepsChange?: (steps: number) => void;
  isInlineOnTablet?: boolean;
}

const DETAILS_PANEL_MAX_SIZE_RATIO = 0.5;
const DETAILS_PANEL_MAX_SIZE = `${DETAILS_PANEL_MAX_SIZE_RATIO * 100}%`;
const PANEL_FLOATING_GUTTER = 4;
const PANEL_FLOATING_MAX_WIDTH = DETAILS_PANEL_WIDTH;
const SEPARATOR_OFFSET = '6px';

export const ObjectDetailsSlot = ({
  panel,
  isOpen,
  isStickyLayout = false,
  stickyTop = 0,
  height = 0,
  onClose,
  onExpansionStepsChange,
  isInlineOnTablet,
}: ObjectDetailsSlotProps) => {
  const { isFloating, hasBackdrop, workspacePadding } = useObjectDetailsSlot({
    isOpen,
    onClose,
    isInlineOnTablet,
  });
  const theme = useTheme();
  const { classes, cx } = useResizablePanelStyles({ separatorOffset: SEPARATOR_OFFSET });

  if (isFloating) {
    return (
      <DetailsPanelDisplayContext.Provider value={{ isFloating, hasBackdrop }}>
        {hasBackdrop && (
          <Backdrop
            open={isOpen}
            onClick={onClose}
            position="absolute"
            zIndex={theme.zIndex.appBar - 2}
          />
        )}
        <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
          <Box
            sx={(theme) => ({
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              width: `calc(100% - ${theme.spacing(PANEL_FLOATING_GUTTER)})`,
              maxWidth: PANEL_FLOATING_MAX_WIDTH,
              zIndex: theme.zIndex.appBar - 1,
            })}
          >
            {panel}
          </Box>
        </Slide>
      </DetailsPanelDisplayContext.Provider>
    );
  }

  const gutter = theme.spacing(workspacePadding);
  const minPanelSize = DETAILS_PANEL_WIDTH + parseFloat(gutter);

  const handleResize = (size: PanelSize) => {
    onExpansionStepsChange?.(
      getDetailsExpansionSteps({
        size,
        minSizeInPixels: minPanelSize,
        maxSizeRatio: DETAILS_PANEL_MAX_SIZE_RATIO,
      }),
    );
  };

  const stickyStyle: CSSProperties =
    isStickyLayout && height > 0
      ? { position: 'sticky', top: stickyTop, alignSelf: 'flex-start', height }
      : {};

  return (
    <>
      <ResizeSeparator
        className={cx(classes.separator, !isOpen && classes.separatorClosed)}
        disabled={!isOpen}
        label={LangEnum.GLOBAL_DETAILS_PANEL_RESIZE}
        hint={LangEnum.GLOBAL_DETAILS_PANEL_RESIZE_HINT}
        style={stickyStyle}
      />
      <Panel
        id={OBJECT_DETAILS_PANEL_ID}
        defaultSize={minPanelSize}
        minSize={minPanelSize}
        maxSize={DETAILS_PANEL_MAX_SIZE}
        onResize={handleResize}
        style={{
          paddingRight: gutter,
          boxSizing: 'border-box',
          overflow: 'visible',
          ...stickyStyle,
        }}
      >
        {panel}
      </Panel>
    </>
  );
};
